import { Injectable } from "@angular/core";
import { FilesService } from "../files/files.service";
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  delay,
  finalize,
  first,
  forkJoin,
  from,
  map,
  mergeMap,
  Observable,
  of,
  Subject,
  takeUntil,
  tap,
  throwError
} from "rxjs";
import { formatDate } from "@angular/common";
import { FileUpload } from "@shared/models/file-upload.model";
import { FileManagerDoc, isFileManagerDocImageType } from "@shared/models/file-manager/file-manager-doc.model";
import { S3UploadService } from "../files/s3-upload.service";
import { HttpEvent, HttpEventType } from "@angular/common/http";
import { FileManagerUpload } from "@shared/models/file-manager/file-manager-upload.model";
import { FileManagerUploadStatus } from "@shared/models/file-manager/file-manager-upload-status.model";
import { FileUploadStatus } from "@shared/constants/file-manager/file-upload-status";
import {
  FileType,
  FileTypes,
  FileUploadOptions,
  FileUploadType
} from "@app/shared/constants/file-manager/file-upload-options";
import { SpreadsheetsService } from "../files/spreadsheets.service";
import { SpreadsheetAnalysisUsageConsent } from "@app/shared/constants/file-manager/spreadsheet-analysis-usage-consent";
import { FileValidationResponse } from "@shared/models/file-manager/file-validation-response.model";
import {
  fileUploadToFileManagerDoc,
  getReadableFileSize,
  parseFileNameToDisplayName,
  parseShortFileDisplayName
} from "./utils/file-utils";
import { getDifferenceInHours } from "./utils/date-utils";
import { getFileExtension } from "@shared/models/file-manager/file-manager-doc";
import { defaultFileManagerConfig, FileManagerConfig } from "@app/shared/components/file-manager/file-manager";
import { featureFlags } from "@environments/environment";
import * as XLSX from 'xlsx';
import { DateUtils } from "@app/shared/utils/date.utils";

export enum FileNameFormatOption {
  WITH_EXTENSION = 'WITH_EXTENSION',
  WITHOUT_EXTENSION = 'WITHOUT_EXTENSION'
}

@Injectable({
  providedIn: 'root'
})
export class FileManagerService {
  private readonly CSV_MAX_FILE_SIZE = 80000000;
  private readonly EXCEL_MAX_FILE_SIZE = 15000000;
  private readonly IMAGE_MAX_FILE_SIZE = 5000000;
  private readonly POWERPOINT_MAX_FILE_SIZE = 10000000;
  private readonly supportedTextFileTypes = [
    'text/plain',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/pdf',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation'
  ];
  private readonly PDF_UPLOAD_TYPE = 'pdf';
  private readonly DOCX_UPLOAD_TYPE = 'vnd.openxmlformats-officedocument.wordprocessingml.document';
  private readonly XLSX_UPLOAD_TYPE = 'vnd.openxmlformats-officedocument.spreadsheetml.sheet';
  private readonly PPTX_UPLOAD_TYPE = 'vnd.openxmlformats-officedocument.presentationml.presentation';

  private readonly supportedSpreadsheetsFileTypes = [
    'text/csv',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-excel'
  ];

  private readonly supportedImageFileTypes = [
    'image/png',
    'image/jpg',
    'image/jpeg',
    'image/webp'
  ];

  private readonly PPTX_EXTENSION = '.pptx';
  private _fileManagerConfig: FileManagerConfig = defaultFileManagerConfig;

  selectedFileType$ = new BehaviorSubject<FileUploadType>(FileUploadOptions['TEXT']);
  allFiles$ = new BehaviorSubject<FileManagerDoc[]>([]);
  selectedFiles$ = new BehaviorSubject<FileManagerDoc[]>([]);
  preSelectedFiles$ = new BehaviorSubject<FileManagerDoc[]>([]);
  uploadedFiles$ = new BehaviorSubject<FileManagerUpload[]>([]);
  private pendingSelectionFiles = new BehaviorSubject<{ [key: string]: boolean }>({});
  private _uploadsInProgress$ = new BehaviorSubject<number>(0);
  public uploadsInProgress$ = this._uploadsInProgress$.asObservable();
  private visualRemovalSubject = new Subject<string>();
  visualRemoval$ = this.visualRemovalSubject.asObservable();

  

  private xlsConversionCompleted$ = new Subject<{ original: File, converted: File, upload: FileManagerUpload }>();

  readonly fileStatusIds = {
    processed: new Array<string>(),
    error: new Array<string>()
  };

  private _isSpreadsheetUsageAcknowledged: boolean | undefined;

  constructor(
    private filesService: FilesService,
    private spreadsheetsService: SpreadsheetsService,
    private s3UploadService: S3UploadService,
  ) {
    this.isSpreadsheetUsageAcknowledged()
      .pipe(
        first()
      ).subscribe({
      next: (isAcknowledged) => this._isSpreadsheetUsageAcknowledged = isAcknowledged
    });
  };

  get onXlsConversionCompleted$(): Observable<{ original: File, converted: File, upload: FileManagerUpload }> {
    return this.xlsConversionCompleted$.asObservable();
  }

  setSelectedFileType(fileType: FileUploadType): void {
    this.selectedFileType$.next(fileType);
  }

  cleanSelectedAndSetFileTypeToImage(): void {
    this.selectedFiles$.next([]);
    this.selectedFileType$.next(FileUploadOptions['IMAGE']);
  }

  private hasBeenProcessingForMoreThanOneHour(file: FileUpload, fileManagerDoc: FileManagerDoc) {
    return fileManagerDoc.status === 'PROCESSING' && file.updatedAt && getDifferenceInHours(file.updatedAt) >= 1
  }

  get isUnsupportedFileSelected$(): Observable<{ file: FileManagerDoc, isUnsupported: boolean }[]> {
    return this.selectedFileType$.pipe(
      mergeMap((type) => this.selectedFiles$.pipe(
        map((files) => files.map((file) => ({
          file,
          isUnsupported: type.unsupportedFileTypes.includes(file.extension)
        })))
      ))
    );
  };

  getLazyFiles$(fileType: FileUploadType, offset: number = 0, sortBy: string = "processed",
                sortDirection: string = "DESC", searchTerm: string = ''): Observable<FileManagerDoc[]> {
    if (this.selectedFileType$.value !== fileType && offset === 0) {
      this.allFiles$.next([]);
    }

    this.setSelectedFileType(fileType);

    return this.filesService.lazyList(fileType.extensions.join(','), offset, sortBy, sortDirection, searchTerm)
      .pipe(
        map((files) => {
          return files.map((file) => {
            const parsedFile = fileUploadToFileManagerDoc(file, 60);

            if (parsedFile.status === 'ERROR') {
              this.addToFileStatusError(parsedFile);
            } else if (parsedFile.status === 'IMAGE_LIMIT_EXCEEDED') {
              this.addToFileStatusError(parsedFile);
            } else if (parsedFile.status === 'PROCESSED') {
              this.updateProcessedFileStatus(parsedFile);
            } else if (this.hasBeenProcessingForMoreThanOneHour(file, parsedFile)) {
              this.addToFileStatusError(parsedFile);
            } else {
              this.fetchProcessingStatus(parsedFile.id!, fileType);
            }

            return parsedFile;
          });
        }),
        tap((files) => {
          if (offset === 0) {
            this.allFiles$.next(files);
          } else {
            this.allFiles$.next(this.allFiles$.value.concat(files));
          }
        })
      );
  };

  getFiles$(fileType: FileUploadType, sortBy: string = "processed", sortDirection: string = "DESC", excludeStatus = ""): Observable<FileManagerDoc[]> {
    return this.filesService.list(
      fileType.extensions.join(','),
      sortBy,
      sortDirection,
      excludeStatus
    ).pipe(
      map((files) => {
        return files.map((file) => {
          const parsedFile = fileUploadToFileManagerDoc(file, 65);

          if (parsedFile.status === 'ERROR') {
            this.addToFileStatusError(parsedFile);
          } else if (parsedFile.status === 'IMAGE_LIMIT_EXCEEDED') {
            this.addToFileStatusError(parsedFile);
          } else if (parsedFile.status === 'PROCESSED') {
            this.updateProcessedFileStatus(parsedFile);
          } else {
            this.fetchProcessingStatus(parsedFile.id!, fileType);
          }

          return parsedFile;
        });
      }),
      tap((files) => this.allFiles$.next(files))
    );
  }

  getFiles(fileIds: string[]): Observable<FileManagerDoc[]> {
    if (!fileIds.length) {
      return of([]);
    }

    const fileObservables = fileIds.map((id) => {
      return this.filesService.get(id).pipe(
        map((file) => {
          const parsedFile = fileUploadToFileManagerDoc(file, 65);

          if (parsedFile.status === 'ERROR') {
            this.addToFileStatusError(parsedFile);
          } else if (parsedFile.status === 'IMAGE_LIMIT_EXCEEDED') {
            this.addToFileStatusError(parsedFile);
          } else if (parsedFile.status === 'PROCESSED') {
            this.updateProcessedFileStatus(parsedFile);
          } else {
            this.fetchProcessingStatus(parsedFile.id!, this.getCategoryForFileType(file.fileType!)!);
          }
          return parsedFile;
        }),
        catchError(() => {
          console.error(`Error fetching file with ID ${id}`);
          return of(null);
        })
      );
    });

    return forkJoin(fileObservables).pipe(
      map((files) => files.filter((file): file is FileManagerDoc => file !== null)),
      tap((files) => this.setSelectedFiles(files))
    );
  };

  getFilesByType(fileType: string) {
    switch (fileType) {
      case 'text':
        return this.supportedTextFileTypes;
      case 'spreadsheets':
        return this.supportedSpreadsheetsFileTypes;
      case 'image':
        return this.supportedImageFileTypes;
      default:
        return [];
    }
  };

  validateFileUploadBatch(files: FileList, fileType: FileType): FileValidationResponse {
    let validationResponse = this.getSupportedFilesValidationResponse(fileType, files);
    this.validateMaxFileSizeForSpreadsheets(fileType, files, validationResponse);
    this.validateTextFiles(fileType, files, validationResponse);
    return validationResponse;
  };

  private validateTextFiles(fileType: string, files: FileList, validationResponse: FileValidationResponse): FileValidationResponse {
    if (fileType === 'text') {
      for (let i = 0; i < files.length; i++) {
        if (getFileExtension(files.item(i)!.name) === this.PPTX_EXTENSION && this.fileLimitExceeded(files.item(i)!)) {
          validationResponse.maxFileSizeExceeded = true;
          validationResponse.unsupportedFileType = false;
          validationResponse.maxFileSizeExceededIndex.push(i)
        }
      }
    }
    return validationResponse
  }

  private getSupportedFilesValidationResponse(fileType: string, files: FileList): FileValidationResponse {
    const supportedFileTypes = this.getFilesByType(fileType);
    const validationResponse = {
      isValid: true,
      unsupportedFileType: false,
      maxFileSizeExceeded: false,
      passwordProtected: false,
      maxFileSizeExceededIndex: [] as number[]
    };

    for (let i = 0; i < files.length; i++) {
      if (!supportedFileTypes.includes(files.item(i)!.type)) {
        validationResponse.isValid = false;
        validationResponse.unsupportedFileType = true;
      }
    }
    return new FileValidationResponse(validationResponse);
  }

  private validateMaxFileSizeForSpreadsheets(fileType: string, files: FileList, validationResponse: FileValidationResponse) {
    for (let i = 0; i < files.length; i++) {
      if ((fileType === 'spreadsheets' || fileType === 'image') && this.fileLimitExceeded(files.item(i)!)) {
        validationResponse.maxFileSizeExceeded = true;
        validationResponse.unsupportedFileType = false;
        validationResponse.maxFileSizeExceededIndex.push(i);
      }
    }
  }

  validFileName(fileName: string): boolean {
    if (!fileName) {
      return false;
    }
    const nameWithoutExtension = fileName.split('.').slice(0, -1).join('.');
    return nameWithoutExtension.trim().length > 0;
  }

  private async convertXLStoXLSX(file: File): Promise<File> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const data = e.target?.result;
          const workbook = XLSX.read(data, { type: 'array' });

          const xlsxData = XLSX.write(workbook, {
            bookType: 'xlsx',
            type: 'array'
          });

          const xlsxBlob = new Blob([xlsxData], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          });

          const fileName = file.name.replace(/\.xls$/i, '.xlsx');
          const xlsxFile = new File([xlsxBlob], fileName, {
            type: xlsxBlob.type,
            lastModified: Date.now()
          });

          resolve(xlsxFile);
        } catch (error) {
          reject(error);
        }
      };

      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });
  }

  uploadFiles(files: File[], uploadType: FileUploadType, imageAnalysis: boolean = false): FileManagerUpload[] {
    const fileUploads: FileManagerUpload[] = [];

    files.forEach(file => {
      const existingFile = this.findExistingFileByNameAndImageAnalysis(file.name, imageAnalysis);

      if (existingFile) {
        this.removeFileFromAllObservables(existingFile.id!);
        this.removeUploadedFile(existingFile.id!);
      }

      this.markFileForAutoSelection(file.name);

      if (file.name.toLowerCase().endsWith('.xls') && !file.name.toLowerCase().endsWith('.xlsx')) {
        const xlsxUpload = this.createDelayedXLSUpload(file, uploadType);
        fileUploads.push(xlsxUpload);
      } else {
        const processedFile = uploadType === FileUploadOptions['IMAGE']
          ? this.getCustomFileName(file)
          : file;

        const fileUpload = new FileUpload(
          undefined,
          processedFile.name,
          undefined,
          undefined,
          undefined,
          undefined,
          processedFile.size,
          imageAnalysis
        );

        let id: string;
        const upload = this.filesService.create(fileUpload).pipe(
          tap((uploadedFile: FileUpload) => {
            id = uploadedFile.id!;
          }),
          mergeMap(uploadedFile => this.s3UploadService.uploadFile(processedFile, uploadedFile.url!)),
          mergeMap(event => this.mapProgress(id, event, uploadType)),
          finalize(() => {
            this.clearAutoSelectionMark(file.name);
          })
        );

        fileUploads.push({ file: processedFile, upload, imageAnalysis });
      }
    });

    return fileUploads;
  }

  addToMainList(fileUpload: FileManagerUpload): void {
    const fileDoc = this.convertUploadToFileManagerDoc({
      file: fileUpload.file,
      id: fileUpload.id!,
      status: FileUploadStatus.COMPLETED,
      progress: 100,
      imageAnalysis: fileUpload.imageAnalysis
    });
    this.allFiles$.next([...this.allFiles$.value, fileDoc]);
  }

  private createDelayedXLSUpload(file: File, uploadType: FileUploadType): FileManagerUpload {
    const upload = from(this.convertXLStoXLSX(file)).pipe(
        mergeMap((xlsxFile) => {
          const fileUpload = new FileUpload(undefined, xlsxFile.name, undefined, undefined, undefined, undefined, xlsxFile.size);

          let id: string;
          return this.filesService.create(fileUpload).pipe(
              tap((uploadedFile: FileUpload) => id = uploadedFile.id!),
              mergeMap((uploadedFile: FileUpload) => this.s3UploadService.uploadFile(xlsxFile, uploadedFile.url!)),
              mergeMap((event: HttpEvent<any>) => this.mapProgress(id, event, uploadType)),
              tap((uploadStatus: FileManagerUploadStatus) => {
                if (uploadStatus.status === FileUploadStatus.COMPLETED) {
                  const completedUpload: FileManagerUpload = {
                    file: xlsxFile,
                    id: uploadStatus.id,
                    status: uploadStatus.status,
                    progress: uploadStatus.progress
                  };
                  this.xlsConversionCompleted$.next({
                    original: file,
                    converted: xlsxFile,
                    upload: completedUpload
                  });
                }
              })
          );
        }),
        catchError((error) => {
          console.error('Error converting XLS to XLSX:', error);
          const fileUpload = new FileUpload(undefined, file.name, undefined, undefined, undefined, undefined, file.size);

          let id: string;
          return this.filesService.create(fileUpload).pipe(
              tap((uploadedFile: FileUpload) => id = uploadedFile.id!),
              mergeMap((uploadedFile: FileUpload) => this.s3UploadService.uploadFile(file, uploadedFile.url!)),
              mergeMap((event: HttpEvent<any>) => this.mapProgress(id, event, uploadType))
          );
        })
    );

    return { file, upload, imageAnalysis: false };
  }

  getFileUploadStatus(data: FileManagerUpload): FileManagerUpload {
    const cancel$: Subject<boolean> = new Subject<boolean>();

    const upload: FileManagerUpload = {
      file: data.file,
      status: FileUploadStatus.IN_PROGRESS,
      progress: 0,
      imageAnalysis: data.imageAnalysis ?? false,
      cancel: () => cancel$.next(true),
      fetch: () => data.upload!.pipe(
        takeUntil(cancel$),
        catchError((err) => {
          upload.status = FileUploadStatus.ERROR;
          upload.progress = 0;
          return throwError(() => err);
        }),
        tap((uploadStatus) => {
          upload.id = uploadStatus.id!;
          upload.status = uploadStatus.status;
          upload.progress = uploadStatus.progress;
        })
      ).subscribe()
    };
    upload.fetch!();
    return upload;
  };

  private mapProgress(id: string, event: HttpEvent<any>, uploadType: FileUploadType): Observable<FileManagerUploadStatus> {
    if (event.type === HttpEventType.Response) {
      return of({
        id,
        status: FileUploadStatus.COMPLETED,
        progress: 100
      });
    }

    if (event.type === HttpEventType.UploadProgress) {
      return of({
        id,
        status: FileUploadStatus.IN_PROGRESS,
        progress: event.total
          ? Math.round((100 * event.loaded) / event.total)
          : 0
      });
    }

    this.fetchProcessingStatus(id, uploadType);
    return of({
      id,
      status: FileUploadStatus.IN_PROGRESS,
      progress: 0
    });
  };

  private fetchProcessingStatus(id: string, uploadType: FileUploadType): void {
    if (uploadType === FileUploadOptions['SPREADSHEETS'] || uploadType === FileUploadOptions['IMAGE']) {
      this.fileStatusIds.processed.push(id);
      return;
    }

    this.filesService.get(id)
      .pipe(
        mergeMap((file) => of(file).pipe(
          delay(3000),
          catchError((err) => of({ ...err, status: 'ERROR' }))
        )),
        tap((file: FileUpload) => {
          switch (file.status) {
            case 'PROCESSED':
              this.fileStatusIds.processed.push(id);
              break;
            case 'ERROR':
              this.addToFileStatusError({ id } as FileManagerDoc);
              this.removeSelectedFile(id);
              break;
            case 'IMAGE_LIMIT_EXCEEDED':
              this.updateFileStatusAcrossObservables(id, 'IMAGE_LIMIT_EXCEEDED', true);
              this.addToFileStatusError({ id } as FileManagerDoc);
              this.removeSelectedFile(id);
              break;
            default:
              this.fetchProcessingStatus(id, uploadType);
              break;
          }
        })
      ).subscribe();
  };

  setSelectedFiles(files: FileManagerDoc[]): void {

    const validFiles = files.filter(file => file.status !== 'ERROR');

    validFiles.forEach((file) => {
      if (!this.fileStatusIds.error.includes(file.id!) && file.status === 'PROCESSED') {
        this.fileStatusIds.processed.push(file.id!);
      }
    });

    this.selectedFiles$.next(validFiles);
  };

  addPreSelectedFile(file: FileManagerDoc): void {
    const preSelectedFiles = this.preSelectedFiles$.value;
    if (preSelectedFiles.some((f) => f.id === file.id || (f.name === file.name && f.imageAnalysis === file.imageAnalysis))) {
      return;
    }
    preSelectedFiles.push(file);
    this.preSelectedFiles$.next(preSelectedFiles);
    this.updateAllFiles(file);
  };

  private updateAllFiles(file: FileManagerDoc) {
    const allFiles = this.allFiles$.value;
    if (allFiles.some((f) => f.id === file.id)) {
      const fileIndex = allFiles.findIndex((f) => f.id === file.id);
      allFiles[fileIndex].isSelected = true;
    } else {
      allFiles.unshift(file);
    }
    this.allFiles$.next(allFiles);
  }

  removePreSelectedFile(arg?: string | FileManagerDoc): void {
    if (arg) {
      if (typeof arg === 'string') {
        const files = this.preSelectedFiles$.value.filter((f) => f.id !== arg);
        this.preSelectedFiles$.next(files);
      } else {
        const files = this.preSelectedFiles$.value.filter((f) => f.id !== arg.id);
        this.preSelectedFiles$.next(files);
      }
    }
  }

  removeSelectedFile(arg?: string | FileManagerDoc): void {
    if (arg) {
      if (typeof arg === 'string') {
        const files = this.selectedFiles$.value.filter((f) => f.id !== arg);
        this.selectedFiles$.next(files);
      } else {
        const files = this.selectedFiles$.value.filter((f) => f.id !== arg.id);
        this.selectedFiles$.next(files);
      }
    }
  }

  reset(): void {
    this.clearPreSelectedFiles();
    this.clearSelectedAndSetType();
  }

  clearSelectedAndSetType() {
    this.selectedFiles$.next([]);
    this.selectedFileType$.next(FileUploadOptions['TEXT']);
  }

  clearSelectedFiles(): void {
    let initialFileType = this.selectedFileType$.value;
    this.selectedFileType$.subscribe((newValue) => {
      if (newValue !== initialFileType) {
        this.preSelectedFiles$.next([]);
        this.selectedFiles$.next([]);
      } else {
        this.selectedFiles$.next(this.selectedFiles$.value);
      }
    });
  };

  private updateProcessedFileStatus(file: FileManagerDoc): void {
    if (!this.fileStatusIds.processed.includes(file.id!)) {
      this.fileStatusIds.processed.push(file.id!);
    }
  };

  private addToFileStatusError(file: FileManagerDoc): void {
    if (!this.fileStatusIds.error.includes(file.id!)) {
      this.fileStatusIds.error.push(file.id!);
      this.removeSelectedFile(file.id!);
    }
  };

  private updateFileStatusAcrossObservables(fileId: string, status: string, isSelectDisabled: boolean = true): void {
    // Update allFiles$
    const allFiles = this.allFiles$.value.map(file =>
      file.id === fileId ? { ...file, status, isSelectDisabled } : file
    );
    this.allFiles$.next(allFiles);

    // Update preSelectedFiles$
    const preSelectedFiles = this.preSelectedFiles$.value.map(file =>
      file.id === fileId ? { ...file, status, isSelectDisabled } : file
    );
    this.preSelectedFiles$.next(preSelectedFiles);

    // Update selectedFiles$
    const selectedFiles = this.selectedFiles$.value.map(file =>
      file.id === fileId ? { ...file, status, isSelectDisabled } : file
    );
    this.selectedFiles$.next(selectedFiles);
  }

  getParseFileNameToDisplayName(name: string, maxFileNameLength: number, formatOption: FileNameFormatOption = FileNameFormatOption.WITH_EXTENSION): string {
    return parseFileNameToDisplayName(name, maxFileNameLength, formatOption);
  }

  convertUploadToFileManagerDoc(upload: FileManagerUpload): FileManagerDoc {
    const file = {
      id: upload.id!,
      name: upload.file.name,
      displayName: parseFileNameToDisplayName(upload.file.name, 50),
      miniDisplayName: parseFileNameToDisplayName(upload.file.name, 30),
      shortDisplayName: parseShortFileDisplayName(upload.file.name),
      size: getReadableFileSize(upload.file.size),
      extension: getFileExtension(upload.file.name),
      date: formatDate(new Date(), 'MMM-dd-yyyy', 'en-US'),
      isSelected: true,
      status: FileUploadStatus.IN_PROGRESS,
      isSelectDisabled: false,
      isUnsupported: false,
      url: '',
      lastUsedAt: formatDate(new Date(), 'MMM-dd-yyyy', 'en-US'),
      imageAnalysis: upload.imageAnalysis ?? false
    };
    this.addPreSelectedFile(file);
    return file;
  }

  isSpreadsheetUsageAcknowledged(): Observable<boolean> {
    if (this._isSpreadsheetUsageAcknowledged !== undefined) {
      return of(this._isSpreadsheetUsageAcknowledged);
    }

    return this.spreadsheetsService.getUsageAcknowledgement()
      .pipe(
        map((usage) => usage.state === SpreadsheetAnalysisUsageConsent.ACKNOWLEDGED)
      );
  };

  acknowledgeSpreadsheetUsage(): Observable<void> {
    return this.spreadsheetsService.acknowledgeUsage({ state: SpreadsheetAnalysisUsageConsent.ACKNOWLEDGED })
      .pipe(
        tap(() => this._isSpreadsheetUsageAcknowledged = true)
      );
  };

  fileLimitExceeded(file: File): boolean {
    switch (file.type) {
      case 'text/csv':
        return file.size > this.CSV_MAX_FILE_SIZE;
      case 'application/vnd.ms-excel':
        return file.size > this.EXCEL_MAX_FILE_SIZE;
      case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
        return file.size > this.EXCEL_MAX_FILE_SIZE;
      case 'application/vnd.openxmlformats-officedocument.presentationml.presentation':
        return file.size > this.POWERPOINT_MAX_FILE_SIZE;
      case 'image/png':
      case 'image/jpg':
      case 'image/jpeg':
      case 'image/webp':
        return file.size > this.IMAGE_MAX_FILE_SIZE;
      default:
        return false;
    }
  }

  getCategoryForFileType(fileType: string): FileUploadType {
    if (!fileType)
      throw new Error('Invalid file type');

    for (const category in FileTypes) {
      if (Object.values(FileTypes[category]).includes(fileType.toUpperCase())) {
        return FileUploadOptions[category];
      }
    }

    throw new Error(`File type ${fileType} not found`);
  }

  async isFilePasswordProtected(file: File): Promise<boolean> {
    const fileType = file.type.split('/')[1];
    switch (fileType) {
      case this.PDF_UPLOAD_TYPE:
      case this.DOCX_UPLOAD_TYPE:
      case this.XLSX_UPLOAD_TYPE:
      case this.PPTX_UPLOAD_TYPE:
        return await this.checkFileForEncryption(file, fileType);
      default:
        return false;
    }
  }

  async checkFileForEncryption(file: File, type: string): Promise<boolean> {
    try {
      const reader = new FileReader();
      const arrayBuffer = await new Promise<ArrayBuffer>((resolve, reject) => {
        reader.onload = () => resolve(reader.result as ArrayBuffer);
        reader.onerror = (error) => reject(error);
        reader.readAsArrayBuffer(file);
      });

      const blob = new Blob([arrayBuffer], { type });
      const text = await blob.text();
      return text.includes("Encrypt");

    } catch (error) {
      console.error('Error reading uploaded file:', error);
      return false;
    }
  }

  confirmSelectedFiles(): void {
    this.setSelectedFiles(this.preSelectedFiles$.value);
    this.clearPreSelectedFiles();
  }

  getSuccessfullyUploadedFiles(): number {
    return this.uploadedFiles$.value
      .filter(file => file.status === FileUploadStatus.COMPLETED).length;
  }

  getExceededAndProtectedFiles(): number {
    return this.uploadedFiles$.value
      .filter(file => (file.status === FileUploadStatus.PASSWORD_PROTECTED ||
        file.status === FileUploadStatus.FILE_SIZE_EXCEEDED)).length;
  }

  clearPreSelectedFiles(): void {
    this.preSelectedFiles$.next([]);
  }

  handleSelectedFiles(selectedFiles: FileManagerDoc[]): void {
    const nonImageFiles = selectedFiles.filter(file => !isFileManagerDocImageType(file));

    if (featureFlags.imagePrompt) {
      this.cleanUploadedFiles();
    }

    for (let i = 0; i < nonImageFiles.length; i++) {
      const file = nonImageFiles[i];
      file.isSelected = true;
    }

    this.setSelectedFiles(nonImageFiles);
  }

  addUploadedFile(file: FileManagerUpload): void {
    const currentFiles = this.uploadedFiles$.value;
    this.uploadedFiles$.next([...currentFiles, file]);
  }

  removeUploadedFile(arg?: string | FileManagerUpload): void {
    if (arg !== '' || arg !== undefined) {
      if (typeof arg === 'string') {
        const currentFiles = this.uploadedFiles$.value.filter(f => f.id !== arg);
        this.uploadedFiles$.next(currentFiles);
      } else {
        const currentFiles = this.uploadedFiles$.value.filter(f => f !== arg);
        this.uploadedFiles$.next(currentFiles);
      }
    }
  }

  updateUploadedFile(file: FileManagerUpload, status: keyof typeof FileUploadStatus): void {
    const currentFiles = this.uploadedFiles$.value.map(f =>
      f === file ? { ...f, status } : f
    );
    this.uploadedFiles$.next(currentFiles);
  }

  cleanUploadedFiles(): void {
    this.uploadedFiles$.next([]);
  }

  get fileManagerConfig(): FileManagerConfig {
    return this._fileManagerConfig;
  }

  set fileManagerConfig(value: FileManagerConfig) {
    this._fileManagerConfig = value;
  }

  incrementUploadsInProgress() {
    this._uploadsInProgress$.next(this._uploadsInProgress$.value + 1);
  }

  decrementUploadsInProgress() {
    this._uploadsInProgress$.next(Math.max(0, this._uploadsInProgress$.value - 1));
  }

  getTotalUploadsCount(): Observable<number> {
    return combineLatest([
      this.selectedFiles$,
      this.uploadsInProgress$
    ]).pipe(
      map(([selectedFiles, inProgress]) => selectedFiles.length + inProgress)
    );
  }

  private getCustomFileName(file: File): File {
    if (this.supportedImageFileTypes.includes(file.type)) {
      const timestamp = DateUtils.getCompactTimestamp();
      const extension = this.getFileExtensionFromType(file.type) ||
        this.getFileExtensionFromName(file.name) ||
        'png';
      const fileNameWithoutExtension = file.name.split('.').slice(0, -1).join('.') || file.name;
      const randomName = `${fileNameWithoutExtension}_${timestamp}.${extension}`;
      return new File([file], randomName, { type: file.type });
    }
    return file;
  }

  private getFileExtensionFromType(mimeType: string): string | null {
    const typeMap: { [key: string]: string } = {
      'image/png': 'png',
      'image/jpeg': 'jpeg',
      'image/jpg': 'jpg',
      'image/webp': 'webp'
    };
    return typeMap[mimeType] || null;
  }

  private getFileExtensionFromName(filename: string): string | null {
    const parts = filename.split('.');
    return parts.length > 1 ? parts.pop()! : null;
  }

  findExistingFileByNameAndImageAnalysis(name: string, imageAnalysis: boolean = false): FileManagerDoc | undefined {
    return this.allFiles$.value.find(f =>
      f.name === name && f.imageAnalysis === imageAnalysis
    );
  }

  private removeFileFromAllObservables(fileId: string): void {
    this.allFiles$.next(this.allFiles$.value.filter(f => f.id !== fileId));

    this.selectedFiles$.next(this.selectedFiles$.value.filter(f => f.id !== fileId));

    this.preSelectedFiles$.next(this.preSelectedFiles$.value.filter(f => f.id !== fileId));

    this.uploadedFiles$.next(this.uploadedFiles$.value.filter(f => f.id !== fileId));

    this.fileStatusIds.processed = this.fileStatusIds.processed.filter(id => id !== fileId);
    this.fileStatusIds.error = this.fileStatusIds.error.filter(id => id !== fileId);
  }

   private markFileForAutoSelection(filename: string): void {
    const current = this.pendingSelectionFiles.value;
    this.pendingSelectionFiles.next({ ...current, [filename]: true });
  }

  private clearAutoSelectionMark(filename: string): void {
    const current = { ...this.pendingSelectionFiles.value };
    delete current[filename];
    this.pendingSelectionFiles.next(current);
  }

  addToAllFiles(file: FileManagerDoc) {
    this.allFiles$.next([...this.allFiles$.value, file]);
  }

  markForVisualRemoval(fileId: string): void {
    this.visualRemovalSubject.next(fileId);
  }

  areFilesEquivalent(file1: FileManagerDoc, file2: FileManagerDoc): boolean {
    return file1.name === file2.name && file1.imageAnalysis === file2.imageAnalysis;
  }
}
